import React, { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, ASSETS } from "@/lib/api";
import SEO from "@/components/SEO";

const TIME_SLOTS = [
  "12:00", "12:30", "13:00", "13:30", "14:00",
  "19:00", "19:30", "20:00", "20:30", "21:00", "21:30", "22:00",
];

export default function Reserve() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: 2,
    occasion: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const minDate = new Date().toISOString().split("T")[0];

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.date || !form.time || !form.guests) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/reservations", {
        ...form,
        guests: Number(form.guests),
      });
      setSubmitted(res.data);
      toast.success("Demande de réservation envoyée !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Une erreur est survenue. Réessayez.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="reserve-success" className="pt-32 pb-24 min-h-[80vh]">
        <div className="max-w-2xl mx-auto px-6 md:px-10 text-center">
          <div className="w-20 h-20 mx-auto rounded-full bg-[#C04E38]/10 grid place-items-center text-[#C04E38] mb-6">
            <CheckCircle2 size={40} />
          </div>
          <div className="eyebrow">Obrigado !</div>
          <h1 className="text-4xl md:text-5xl mt-3">Votre demande est bien reçue</h1>
          <p className="text-[#5C5A56] mt-5 text-lg">
            Bonjour <b>{submitted.name}</b>, nous reviendrons vers vous très vite
            pour confirmer votre table du <b>{submitted.date}</b> à <b>{submitted.time}</b> pour <b>{submitted.guests}</b> personnes.
          </p>
          <p className="text-sm text-[#5C5A56] mt-3">
            Un email de confirmation a été envoyé à <b>{submitted.email}</b>.
          </p>
          <button
            onClick={() => { setSubmitted(null); setForm({ name: "", email: "", phone: "", date: "", time: "", guests: 2, occasion: "", message: "" }); }}
            className="btn-secondary mt-8"
            data-testid="new-reservation-btn"
          >
            Nouvelle réservation
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="reserve-page">
      <SEO title="Réserver une table" description="Réservez votre table au Globo Dancing à Trappes en 1 minute : choix date, heure, nombre de couverts. Confirmation par email. Tel : 01 30 50 46 14." path="/reserver"/>
      <section className="pt-32 pb-10 azulejo-bg">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 reveal">
          <div className="eyebrow">Reserve a sua mesa</div>
          <h1 className="text-5xl md:text-7xl mt-4 leading-[0.95]">
            Réservez votre <em className="italic text-[#C04E38]">table</em>
          </h1>
          <p className="text-[#5C5A56] text-lg mt-5 max-w-2xl">
            Remplissez le formulaire ci-dessous et nous vous confirmons par email
            dans la journée.
          </p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10 grid lg:grid-cols-5 gap-10">
          <form onSubmit={submit} data-testid="reservation-form" className="lg:col-span-3 card-warm p-8 md:p-10 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Nom complet *</label>
                <input type="text" required value={form.name} onChange={update("name")} className="input-warm" data-testid="reservation-name" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Téléphone *</label>
                <input type="tel" required value={form.phone} onChange={update("phone")} className="input-warm" data-testid="reservation-phone" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Email *</label>
              <input type="email" required value={form.email} onChange={update("email")} className="input-warm" data-testid="reservation-email" />
            </div>

            <div className="grid md:grid-cols-3 gap-5">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Date *</label>
                <input type="date" required min={minDate} value={form.date} onChange={update("date")} className="input-warm" data-testid="reservation-date" />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Heure *</label>
                <select required value={form.time} onChange={update("time")} className="input-warm" data-testid="reservation-time">
                  <option value="">Choisir...</option>
                  {TIME_SLOTS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Couverts *</label>
                <select required value={form.guests} onChange={update("guests")} className="input-warm" data-testid="reservation-guests">
                  {[1,2,3,4,5,6,7,8,9,10,12,15,20].map((n) => <option key={n} value={n}>{n} {n === 1 ? "personne" : "personnes"}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Occasion (optionnel)</label>
              <select value={form.occasion} onChange={update("occasion")} className="input-warm" data-testid="reservation-occasion">
                <option value="">—</option>
                <option>Dîner en famille</option>
                <option>Anniversaire</option>
                <option>Soirée Fado</option>
                <option>Déjeuner d'affaires</option>
                <option>Repas entre amis</option>
                <option>Autre</option>
              </select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1.5 block">Message (allergies, demandes...)</label>
              <textarea rows={4} value={form.message} onChange={update("message")} className="input-warm" data-testid="reservation-message" />
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full md:w-auto" data-testid="reservation-submit">
              {submitting ? <><Loader2 className="animate-spin mr-2" size={18}/> Envoi en cours...</> : "Confirmer la demande"}
            </button>

            <p className="text-xs text-[#5C5A56]">
              * Champs obligatoires. Pour les groupes de plus de 12 personnes, contactez-nous directement par téléphone.
            </p>
          </form>

          <aside className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl overflow-hidden h-64">
              <img src={ASSETS.family} alt="Convivialité" className="w-full h-full object-cover" />
            </div>
            <div className="card-warm p-6">
              <h3 className="text-xl mb-3">Bon à savoir</h3>
              <ul className="space-y-2 text-sm text-[#5C5A56]">
                <li>• Confirmation par email rapide</li>
                <li>• Soirées Fado : vendredi & samedi</li>
                <li>• Privatisation salle du fond à partir de 12 personnes</li>
                <li>• Lundi : fermé</li>
              </ul>
            </div>
            <div className="card-warm p-6 bg-[#1F476B] !text-white border-0">
              <h3 className="text-xl mb-2 text-white">Une question ?</h3>
              <p className="text-white/80 text-sm">Appelez-nous : <a href="tel:0130504614" className="font-bold text-white">01 30 50 46 14</a></p>
              <p className="text-white/70 text-xs mt-1">Tous les jours sauf lundi.</p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
